import { ReactNode, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { FolderDto, MAX_FOLDER_DEPTH } from "@repo/api/folder";
import {
  IconFolderPlus,
  IconFolderSymlink,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { paths } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/lib/components/Popover";
import {
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useMoveFolderMutation,
  useRenameFolderMutation,
} from "@/lib/queries/folder-queries";
import NameDialog from "./NameDialog";
import ConfirmDeleteDialog from "@/lib/components/ConfirmDeleteDialog";
import FolderPicker from "./FolderPicker";
import Toast from "@/lib/components/Toast";

type Props = {
  folder: FolderDto;
  teamExternalId: string;
  folderDepth: number;
  children: ReactNode;
  align?: "start" | "center" | "end";
  anchorClassName?: string;
  onAnchorClick?: (e: React.MouseEvent) => void;
  onDeleteSuccess?: () => void;
};

export default function FolderActionsMenu({
  folder,
  teamExternalId,
  folderDepth,
  children,
  align = "end",
  anchorClassName,
  onAnchorClick,
  onDeleteSuccess,
}: Props) {
  const canCreateSubfolder = folderDepth < MAX_FOLDER_DEPTH;

  const handleCreateSubfolder = () => {
    if (!canCreateSubfolder) {
      Toast.info(
        `You can only nest folders up to ${MAX_FOLDER_DEPTH} levels deep.`,
      );
      return;
    }
    setCreateOpen(true);
  };
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const createFolderMutation = useCreateFolderMutation({
    teamExternalId,
    onSuccess: () => setCreateOpen(false),
  });
  const renameFolderMutation = useRenameFolderMutation({
    teamExternalId,
    onSuccess: () => setRenameOpen(false),
  });
  const moveFolderMutation = useMoveFolderMutation({
    teamExternalId,
    onSuccess: () => setMoveOpen(false),
  });
  const deleteFolderMutation = useDeleteFolderMutation({
    teamExternalId,
    onSuccess: () => setDeleteOpen(false),
  });

  return (
    <>
      <Popover open={moveOpen} onOpenChange={setMoveOpen}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className={anchorClassName}
            onClick={onAnchorClick}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
              <DropdownMenuContent
                align={align}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuItem onSelect={handleCreateSubfolder}>
                  <IconFolderPlus size={16} />
                  New folder
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                  <IconPencil size={16} />
                  Rename folder
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMoveOpen(true)}>
                  <IconFolderSymlink size={16} />
                  Move folder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-error"
                  onSelect={() => setDeleteOpen(true)}
                >
                  <IconTrash size={16} />
                  Remove folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align={align}
          className="w-72 p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchorRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <FolderPicker
            teamExternalId={teamExternalId}
            excludeFolderExternalId={folder.externalId}
            currentLocationExternalId={folder.parentFolderExternalId}
            allowRoot
            placedFolderDepth={1}
            isMoving={moveFolderMutation.isPending}
            onSelect={(targetFolderExternalId) =>
              moveFolderMutation.mutate(
                {
                  folderExternalId: folder.externalId,
                  data: { parentFolderExternalId: targetFolderExternalId },
                },
                {
                  onSuccess: () =>
                    navigate(
                      targetFolderExternalId
                        ? paths.videosFolder(
                            teamExternalId,
                            targetFolderExternalId,
                          )
                        : paths.videos(teamExternalId),
                    ),
                },
              )
            }
          />
        </PopoverContent>
      </Popover>
      <NameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New folder"
        description="Organize your videos by grouping them into folders."
        placeholder="Folder name"
        submitLabel="Create"
        isSubmitting={createFolderMutation.isPending}
        onSubmit={(name) =>
          createFolderMutation.mutate({
            name,
            parentFolderExternalId: folder.externalId,
          })
        }
      />
      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Rename folder"
        description={`Rename "${folder.name}" to something else.`}
        initialName={folder.name}
        placeholder="Folder name"
        submitLabel="Rename"
        isSubmitting={renameFolderMutation.isPending}
        onSubmit={(name) =>
          renameFolderMutation.mutate({
            folderExternalId: folder.externalId,
            data: { name },
          })
        }
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Remove folder"
        description={`"${folder.name}" and all of its subfolders will be removed. Videos inside are not deleted and stay available in your videos.`}
        confirmLabel="Remove"
        isDeleting={deleteFolderMutation.isPending}
        onConfirm={async () => {
          try {
            await deleteFolderMutation.mutateAsync(folder.externalId);
            onDeleteSuccess?.();
          } catch {
            // Error is surfaced by the mutation's onError toast.
          }
        }}
      />
    </>
  );
}
